import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, of, shareReplay, tap, throwError } from 'rxjs';

import { storageStrategy } from '@reforgium/statum';

import { environment } from 'constants/environment';
import { urls } from 'constants/urls.constant';

import { AuthDto, CredentialsDto, UserRole } from 'entities/auth.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = `${environment.API_ENDPOINT}/auth`;

  private http = inject(HttpClient);
  private router = inject(Router);
  private readonly sessionStorage = storageStrategy<'token' | 'refresh-token' | 'role', string>('session');
  private readonly persistentStorage = storageStrategy<'token' | 'refresh-token' | 'role', string>('persist');
  private storage = this.resolveStorage();
  private refreshRequest$: Observable<AuthDto> | null = null;
  private sessionExpired = false;

  get token(): string | null {
    return this.storage.get('token');
  }

  get refreshToken(): string | null {
    return this.storage.get('refresh-token');
  }

  get role(): UserRole | null {
    return this.normalizeRole(this.storage.get('role'));
  }

  get isAuthenticated(): boolean {
    return !!this.token;
  }

  get roles(): UserRole[] {
    const payload = this.decodeTokenPayload();
    const knownRoles = new Set<UserRole>(['USER', 'ADMIN', 'SUPER_ADMIN']);

    return this.collectRoleNames([this.role, payload])
      .map((role) => role.replace(/^ROLE_/, '').toUpperCase())
      .filter(
        (role, index, roles): role is UserRole => knownRoles.has(role as UserRole) && roles.indexOf(role) === index,
      );
  }

  get canManageUsers(): boolean {
    return this.roles.includes('SUPER_ADMIN');
  }

  private set token(value: string | null) {
    value && this.storage.set('token', value);
    !value && this.storage.remove('token');
  }

  private set refreshToken(value: string | null) {
    value && this.storage.set('refresh-token', value);
    !value && this.storage.remove('refresh-token');
  }

  private set role(value: UserRole | null) {
    value && this.storage.set('role', value);
    !value && this.storage.remove('role');
  }

  signIn(credentials: CredentialsDto, rememberMe = false) {
    return this.http.post<AuthDto>(`${this.apiUrl}/sign-in`, credentials).pipe(
      tap((data) => {
        this.clearAuthStorage();
        this.sessionExpired = false;
        this.storage = rememberMe ? this.persistentStorage : this.sessionStorage;
        this.storeAuthData(data);
        this.role = this.normalizeRole(data.role ?? data.user?.role) ?? this.roles[0] ?? null;
      }),
    );
  }

  refresh(): Observable<AuthDto> {
    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    const refreshToken = this.refreshToken;

    if (!refreshToken) {
      return throwError(() => new Error('Refresh token is unavailable'));
    }

    this.refreshRequest$ = this.http.post<AuthDto>(`${this.apiUrl}/refresh`, { refreshToken }).pipe(
      tap((data) => {
        this.storeAuthData(data);

        const role = this.normalizeRole(data.role ?? data.user?.role);

        role && (this.role = role);
      }),
      finalize(() => {
        this.refreshRequest$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.refreshRequest$;
  }

  logout() {
    this.http
      .post<void>(`${this.apiUrl}/logout`, {})
      .pipe(catchError(() => of(undefined)))
      .subscribe(() => this.expire());
  }

  expire(): boolean {
    if (this.sessionExpired) {
      return false;
    }

    this.sessionExpired = true;
    this.clearAuthStorage();

    void this.router.navigate([urls.signIn], { state: { redirectTo: this.router.url } });

    return true;
  }

  private resolveStorage() {
    const sessionHasPair = !!this.sessionStorage.get('token') && !!this.sessionStorage.get('refresh-token');
    const persistentHasPair = !!this.persistentStorage.get('token') && !!this.persistentStorage.get('refresh-token');

    if (sessionHasPair) {
      return this.sessionStorage;
    }

    if (persistentHasPair) {
      return this.persistentStorage;
    }

    if (this.sessionStorage.get('refresh-token')) {
      return this.sessionStorage;
    }

    if (this.persistentStorage.get('refresh-token')) {
      return this.persistentStorage;
    }

    return this.sessionStorage;
  }

  private storeAuthData(data: AuthDto): void {
    if (!data.token || !data.refreshToken) {
      throw new Error('Authentication response does not contain a complete token pair');
    }

    this.token = data.token;
    this.refreshToken = data.refreshToken;
  }

  private clearAuthStorage(): void {
    const keys = ['token', 'refresh-token', 'role'] as const;

    keys.forEach((key) => {
      this.sessionStorage.remove(key);
      this.persistentStorage.remove(key);
    });
  }

  private normalizeRole(value: unknown): UserRole | null {
    if (typeof value !== 'string') {
      return null;
    }

    const role = value.replace(/^ROLE_/, '').toUpperCase();

    return role === 'USER' || role === 'ADMIN' || role === 'SUPER_ADMIN' ? role : null;
  }

  private decodeTokenPayload(): Record<string, unknown> | null {
    if (!this.token) {
      return null;
    }

    try {
      const payload = this.token.split('.')[1];

      if (!payload) {
        return null;
      }

      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const bytes = atob(normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '='));
      const decoded = decodeURIComponent(
        bytes
          .split('')
          .map((character) => `%${`00${character.charCodeAt(0).toString(16)}`.slice(-2)}`)
          .join(''),
      );
      const parsed: unknown = JSON.parse(decoded);

      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }

  private collectRoleNames(value: unknown): string[] {
    if (typeof value === 'string') {
      return value.split(/[\s,]+/).filter(Boolean);
    }

    if (Array.isArray(value)) {
      return value.flatMap((item) => this.collectRoleNames(item));
    }

    if (typeof value === 'object' && value !== null) {
      return Object.values(value).flatMap((item) => this.collectRoleNames(item));
    }

    return [];
  }
}
